import { useState } from 'react';
import { useTranslation } from 'react-i18next';
import { Link } from 'react-router-dom';
import { LOCALE_NATIVE_NAMES, PERMISSIONS, SUPPORTED_LOCALES, type Locale } from '@wms/shared';
import { changeLocale } from '../../lib/i18n';
import { apiFetch } from '../../lib/api';
import { useInvalidateSession, useSession } from '../../lib/session';
import { ChangePasswordPage } from '../auth/change-password-page';
import { SessionsPanel } from './sessions-panel';
import { OverridePinPanel } from './override-pin-panel';
import { WarehousesPanel } from './warehouses-panel';
import { LocationTypesPanel } from './location-types-panel';
import { UomsPanel } from './uoms-panel';

type Tab = 'preferences' | 'security' | 'organization' | 'users';

export function SettingsPage(): React.JSX.Element {
  const { t, i18n } = useTranslation();
  const { data: session } = useSession();
  const invalidateSession = useInvalidateSession();
  const [tab, setTab] = useState<Tab>('preferences');

  const canManageUsers = session?.permissions.includes(PERMISSIONS.USER_MANAGE) ?? false;
  const canManageOrg = session?.permissions.includes(PERMISSIONS.SETTINGS_MANAGE) ?? false;

  const setLanguage = async (locale: Locale): Promise<void> => {
    await changeLocale(locale);
    // 登录后语言偏好写回 profile（架构书 §14："用户偏好...覆盖 localStorage"）
    await apiFetch('/session/me/locale', { method: 'PATCH', body: { locale } });
    await invalidateSession();
  };

  return (
    <div>
      <h1 style={{ fontSize: 'var(--text-2xl)' }}>{t('settings.title')}</h1>
      <div className="tabs" role="tablist">
        <button
          role="tab"
          aria-selected={tab === 'preferences'}
          onClick={() => setTab('preferences')}
        >
          {t('settings.tabMyPreferences')}
        </button>
        <button role="tab" aria-selected={tab === 'security'} onClick={() => setTab('security')}>
          {t('settings.tabSecurity')}
        </button>
        {canManageOrg && (
          <button
            role="tab"
            aria-selected={tab === 'organization'}
            onClick={() => setTab('organization')}
          >
            {t('settings.tabOrganization')}
          </button>
        )}
        {canManageUsers && (
          <button role="tab" aria-selected={tab === 'users'} onClick={() => setTab('users')}>
            {t('settings.tabUsers')}
          </button>
        )}
      </div>

      {tab === 'preferences' && (
        <section className="section">
          <h2>{t('settings.language')}</h2>
          <nav className="row" aria-label={t('shell.language')}>
            {SUPPORTED_LOCALES.map((locale: Locale) => (
              <button
                key={locale}
                type="button"
                className="shell__locale"
                aria-current={i18n.language === locale}
                onClick={() => void setLanguage(locale)}
              >
                {LOCALE_NATIVE_NAMES[locale]}
              </button>
            ))}
          </nav>
        </section>
      )}

      {tab === 'security' && (
        <>
          <section className="section">
            <h2>{t('auth.changePasswordTitle')}</h2>
            <ChangePasswordPage />
          </section>
          <OverridePinPanel />
          <SessionsPanel />
        </>
      )}

      {tab === 'organization' && canManageOrg && (
        <>
          <WarehousesPanel />
          <LocationTypesPanel />
          <UomsPanel />
        </>
      )}

      {tab === 'users' && canManageUsers && (
        <section className="section">
          <div className="row" style={{ gap: 'var(--space-3)' }}>
            <Link to="/settings/users" className="btn btn--ghost">
              {t('users.title')} →
            </Link>
            <Link to="/settings/roles" className="btn btn--ghost">
              {t('users.rolesTitle')} →
            </Link>
          </div>
        </section>
      )}
    </div>
  );
}
